<template>
    <div class="content">
        <base-header class="pb-6">
            <div class="row align-items-center py-4">
                <div class="col-lg-6 col-7">
                    <h6 class="h2 text-white d-inline-block mb-0">
                        {{ $t("menu.app_catalog_promotions") }}
                    </h6>
                    <nav aria-label="breadcrumb" class="d-none d-md-inline-block ml-md-4">
                        <route-bread-crumb></route-bread-crumb>
                    </nav>
                </div>
                <div class="col-lg-6 col-5 text-right">
                    <base-button
                        v-if="isAdmin"
                        size="md"
                        type="neutral"
                        @click="goCreate"
                    >
                        <i class="fa fa-plus"></i>
                        {{ $t("app_catalog.btn_add_campaign") }}
                    </base-button>
                </div>
            </div>
        </base-header>

        <div class="container-fluid mt--6">
            <card class="no-border-card" body-classes="px-0 pb-1" footer-classes="pb-2">
                <template slot="header">
                    <div class="row">
                        <div class="col-md-4">
                            <base-input>
                                <el-select
                                    v-model="statusFilter"
                                    :placeholder="$t('status')"
                                    clearable
                                    @change="loadList"
                                    class="w-100"
                                >
                                    <el-option :label="$t('app_catalog.status_draft')" value="draft"></el-option>
                                    <el-option :label="$t('app_catalog.status_active')" value="active"></el-option>
                                    <el-option :label="$t('app_catalog.status_ended')" value="ended"></el-option>
                                </el-select>
                            </base-input>
                        </div>
                        <div class="col-md-2">
                            <base-button type="primary" size="sm" @click="loadList">
                                <i class="fa fa-search"></i> {{ $t("search") }}
                            </base-button>
                        </div>
                    </div>
                </template>

                <div v-loading="loading">
                    <el-table
                        :data="list"
                        row-key="id"
                        header-row-class-name="thead-light"
                        :empty-text="$t('app_catalog.no_data')"
                    >
                        <!-- The name opens the campaign page for every role (read-only for
                             a non-admin seller — spec §2 / E5). -->
                        <el-table-column :label="$t('name')" prop="name" min-width="160px">
                            <template slot-scope="{ row }">
                                <a href="#" class="font-weight-bold" @click.prevent="goEdit(row)">{{ row.name }}</a>
                            </template>
                        </el-table-column>
                        <el-table-column :label="$t('app_catalog.col_kind')" min-width="120px">
                            <template slot-scope="{ row }">
                                {{ row.kind === "percent_off" ? $t("app_catalog.campaign_kind_percent") : $t("app_catalog.campaign_kind_fixed") }}
                            </template>
                        </el-table-column>
                        <el-table-column :label="$t('app_catalog.col_value')" prop="value" min-width="80px">
                            <template slot-scope="{ row }">
                                {{ row.kind === "percent_off" ? row.value + "%" : formatPrice(row.value) }}
                            </template>
                        </el-table-column>
                        <el-table-column :label="$t('app_catalog.col_starts_at')" prop="starts_at" min-width="130px">
                            <template slot-scope="{ row }">
                                {{ formatDate(row.starts_at) }}
                            </template>
                        </el-table-column>
                        <el-table-column :label="$t('app_catalog.col_ends_at')" prop="ends_at" min-width="130px">
                            <template slot-scope="{ row }">
                                {{ formatDate(row.ends_at) }}
                            </template>
                        </el-table-column>
                        <el-table-column :label="$t('app_catalog.flash_sale_col_products')" min-width="80px" align="center">
                            <template slot-scope="{ row }">
                                {{ row.target_count === undefined || row.target_count === null ? "-" : row.target_count }}
                            </template>
                        </el-table-column>
                        <!-- Period phase, not the raw status: an "active" campaign whose
                             window has passed reads หมดเวลา, not เปิดใช้งาน (spec §6.4). -->
                        <el-table-column :label="$t('status')" prop="status" min-width="90px">
                            <template slot-scope="{ row }">
                                <el-tag :type="phaseTagType(phaseOf(row))" size="small">
                                    {{ $t('app_catalog.phase_' + phaseOf(row)) }}
                                </el-tag>
                            </template>
                        </el-table-column>
                        <!-- ⋮ kebab, same pattern as Products.vue / Categories.vue
                             (owner 2026-10-05: no ID column, one menu per row). -->
                        <el-table-column :label="$t('actions')" width="72px" align="center">
                            <template slot-scope="{ row }">
                                <el-dropdown
                                    trigger="click"
                                    data-test="flash-list-kebab"
                                    @command="cmd => onRowCommand(cmd, row)"
                                >
                                    <base-button
                                        size="sm"
                                        type="secondary"
                                        class="btn-icon-only"
                                        :title="$t('actions')"
                                        @click.native.stop
                                    >
                                        <i class="fa fa-ellipsis-v"></i>
                                    </base-button>
                                    <el-dropdown-menu slot="dropdown">
                                        <el-dropdown-item command="edit">
                                            <i class="fa mr-2 text-info" :class="isAdmin ? 'fa-edit' : 'fa-eye'"></i>{{ isAdmin ? $t("app_catalog.edit_campaign") : $t("app_catalog.flash_sale_view") }}
                                        </el-dropdown-item>
                                        <el-dropdown-item v-if="isAdmin" command="delete" divided>
                                            <span class="text-danger">
                                                <i class="fa fa-trash mr-2"></i>{{ $t("app_catalog.btn_delete") }}
                                            </span>
                                        </el-dropdown-item>
                                    </el-dropdown-menu>
                                </el-dropdown>
                            </template>
                        </el-table-column>
                    </el-table>
                </div>

                <template slot="footer">
                    <div class="d-flex justify-content-end mt-3">
                        <el-pagination
                            background
                            layout="prev, pager, next"
                            :total="total"
                            :page-size="pageSize"
                            :current-page="page"
                            @current-change="onPageChange"
                        ></el-pagination>
                    </div>
                </template>
            </card>
        </div>

    </div>
</template>

<script>
import { apiErrorMessage } from "@/util/api-error";
import {
    Select,
    Option,
    Table,
    TableColumn,
    Tag,
    Pagination,
    Dropdown,
    DropdownMenu,
    DropdownItem
} from "element-ui";
import { campaignPhase, phaseTagType, formatBangkok } from "@/utils/flashCampaign";
import { mapGetters, mapActions } from "vuex";
import swal from "sweetalert2";
import refreshOnArrival from "@/mixins/refreshOnArrival";
import { consumeRefresh } from "@/utils/pageRefresh";

export default {
    name: "AppCatalogPromotions",
    mixins: [refreshOnArrival],
    components: {
        [Select.name]: Select,
        [Option.name]: Option,
        [Table.name]: Table,
        [TableColumn.name]: TableColumn,
        [Tag.name]: Tag,
        [Pagination.name]: Pagination,
        [Dropdown.name]: Dropdown,
        [DropdownMenu.name]: DropdownMenu,
        [DropdownItem.name]: DropdownItem
    },
    data() {
        return {
            statusFilter: "",
            page: 1,
            pageSize: 20
        };
    },
    computed: {
        ...mapGetters({
            list: "AppCatalogCampaign/list",
            total: "AppCatalogCampaign/total",
            loading: "AppCatalogCampaign/loading",
            isAdmin: "Auth/isAdmin"
        })
    },
    mounted() {
        // Fresh mount is already fresh: drop a stale flag so the next activation does not refetch.
        consumeRefresh(this.$route.fullPath);
        this.loadList();
    },
    methods: {
        // refreshOnArrival mixin: a sender asked for a refresh before navigating here.
        reloadOnArrival() {
            this.loadList();
        },
        ...mapActions({
            fetchList: "AppCatalogCampaign/fetchList",
            removeItem: "AppCatalogCampaign/remove"
        }),
        loadList() {
            this.fetchList({
                status: this.statusFilter,
                page: this.page,
                limit: this.pageSize
            });
        },
        onPageChange(p) {
            this.page = p;
            this.loadList();
        },
        // Create and edit are a page now (spec §2), not a dialog: the products
        // table there is paged, and the old dialog's PUT replaced every target.
        goCreate() {
            this.$router.push({ name: "AppCatalogFlashSaleNew" });
        },
        goEdit(row) {
            if (!row || !row.id) return;
            this.$router.push({ name: "AppCatalogFlashSaleEdit", params: { id: String(row.id) } });
        },
        onRowCommand(cmd, row) {
            if (cmd === "edit") return this.goEdit(row);
            if (cmd === "delete" && this.isAdmin) return this.confirmDelete(row);
        },
        phaseOf(row) {
            return campaignPhase(row);
        },
        phaseTagType(phase) {
            return phaseTagType(phase);
        },
        confirmDelete(row) {
            swal.fire({
                title: this.$t("app_catalog.delete_title"),
                text: this.$t("app_catalog.campaign_delete_warning") || this.$t("app_catalog.delete_msg"),
                icon: "warning",
                showCancelButton: true,
                reverseButtons: true,
                focusCancel: true,
                confirmButtonText: this.$t("app_catalog.btn_delete"),
                cancelButtonText: this.$t("app_catalog.btn_cancel"),
                confirmButtonColor: "#d33",
                cancelButtonColor: "#6c757d"
            }).then(async (result) => {
                if (result.value) {
                    try {
                        await this.removeItem(row.id);
                        this.loadList();
                        this.$notify({ type: "success", message: this.$t("app_catalog.delete_success") });
                    } catch (err) {
                        const msg = apiErrorMessage(err, this.$t);
                        this.$notify({ type: "danger", message: msg });
                    }
                }
            });
        },
        formatPrice(v) {
            return Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        },
        // Bangkok wall-clock whatever the machine's timezone (spec §3) — the
        // same clock the campaign page edits in.
        formatDate(dateStr) {
            return formatBangkok(dateStr);
        }
    }
};
</script>
